import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { HandThumbUpIcon } from '@heroicons/react/24/outline';
import { formatNumberCompact } from '@shared/dream-components';
import cx from 'classnames';

import { HoverPopover } from '~/components/HoverPopover';
import type { CommunityReactionType } from '~/interfaces/community';
import { REACTION_EMOJI } from '~/interfaces/community';

import { ReactionTypeButtons } from '../../ui/ReactionTypeButtons';

import {
  preventNativeTouchMenu,
  touchSuppressionClass,
  touchSuppressionStyle,
  useLongPressTrigger,
} from '../../../_hooks/useLongPressTrigger';
import { actionIconClass, countClass } from '../constants';
import { useReactableReactions } from './useReactableReactions';

const triggerLayoutClass = 'flex items-center gap-1.5 h-8 px-2 py-1';

export type CommunityReactionPickerProps = {
  reactableType: 'CommunityPost' | 'CommunityPostComment';
  reactableId: string;
  reactionsCount: Partial<Record<CommunityReactionType, number>>;
  memberReactions: CommunityReactionType[];
};

export const ReactionPicker: FC<CommunityReactionPickerProps> = ({
  reactableType,
  reactableId,
  reactionsCount,
  memberReactions,
}) => {
  const { t } = useTranslation();
  const { permissions, localMember, reactionEntries, totalReactions, handleReactionClick } = useReactableReactions({
    reactableType,
    reactableId,
    reactionsCount,
    memberReactions,
  });
  const { isOpen, handleOpenChange, triggerHandlers } = useLongPressTrigger({
    onShortTap: () => selectReaction('like'),
  });
  const selectReaction = (reactionType: CommunityReactionType) => {
    handleOpenChange(false);
    void handleReactionClick(reactionType);
  };

  const triggerContent =
    reactionEntries.length > 0 ? (
      <>
        <div className="flex items-center isolate pr-1">
          {reactionEntries.map((reaction, index) => (
            <div
              key={reaction.type}
              className={cx(
                'flex items-center justify-center size-4 rounded-full bg-[#ededed] border border-white text-xs leading-none',
                { '-ml-1': index > 0 },
              )}
              style={{ zIndex: reactionEntries.length - index }}
            >
              {REACTION_EMOJI[reaction.type]}
            </div>
          ))}
        </div>
        <span className={countClass}>{formatNumberCompact(totalReactions)}</span>
      </>
    ) : (
      <HandThumbUpIcon className={actionIconClass} />
    );

  // Banned (restricted) members keep seeing counts but get no affordance.
  // Guests stay interactive on purpose: clicking prompts the join modal.
  if (!permissions.canReact && !permissions.isGuest) {
    return <div className={triggerLayoutClass}>{triggerContent}</div>;
  }

  return (
    <HoverPopover
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      popoverStyle={touchSuppressionStyle}
      onPopoverContextMenu={preventNativeTouchMenu}
      triggerClassName={cx(
        'flex items-center rounded-full hover:bg-black/5 transition-colors cursor-pointer',
        touchSuppressionClass,
      )}
      popoverClassName={cx(
        'flex gap-1 rounded-full p-2 bg-white/80 backdrop-blur-[15px] shadow-[0px_4px_12px_rgba(0,0,0,0.08)] border-[0.5px] border-white isolate z-10',
        touchSuppressionClass,
      )}
      trigger={
        <button
          type="button"
          aria-label={t('community.reactions.react')}
          className={cx(triggerLayoutClass, touchSuppressionClass)}
          style={touchSuppressionStyle}
          {...triggerHandlers}
        >
          {triggerContent}
        </button>
      }
    >
      <ReactionTypeButtons ownReactionTypes={localMember} onReact={selectReaction} />
    </HoverPopover>
  );
};
